/* eslint-disable prefer-const */
//* config, interfaces, services
import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EDITOR_CUBOID_DIRECTIONS, EDITOR_POINTS_DISTANCE_THRESHOLD, EDITOR_SHAPE_KEYS } from '@editor/config/constants'
import {
  Cuboid,
  CuboidDirection,
  CylinderDetectionResult,
  PointArray,
  PolygonDetectionResult,
  ShapeDetectionResult,
  ShapeKey,
} from '@editor/interfaces/interfaces'
import {
  Box3,
  BoxGeometry,
  Camera,
  EdgesGeometry,
  Float32BufferAttribute,
  Intersection,
  Line3,
  Matrix3,
  Matrix4,
  Object3D,
  Object3DEventMap,
  Plane,
  Points,
  Quaternion,
  Raycaster,
  Vector2,
  Vector3,
} from 'three'

import { roundNumber } from './Util'

/**
 * 選択された点のポジション情報を返す
 * @param {Points} point 点群データ
 * @param {number} pointIndex 選択された点の要素番号
 * @return {PointArray} [number, number, number]
 */
export const createSelectedPoint = (
  points: Points,
  intersection: Intersection<Object3D<Object3DEventMap>>,
): PointArray | undefined => {
  if (intersection.index !== undefined && intersection.index >= 0) {
    const position: Float32BufferAttribute = points.geometry.getAttribute('position') as Float32BufferAttribute

    //* 選択座標は小数点以下6桁に変換
    return [
      roundNumber(position.getX(intersection.index), '0.000001'),
      roundNumber(position.getY(intersection.index), '0.000001'),
      roundNumber(position.getZ(intersection.index), '0.000001'),
    ]
  }

  if (intersection.faceIndex !== undefined && intersection.faceIndex !== null && intersection.faceIndex >= 0) {
    return intersection.point.toArray().map((point) => roundNumber(point, '0.000001')) as [number, number, number]
  }

  return undefined
}

/**
 * 点群の表示パラメータを設定
 * @param {boolean} isVisible true: 表示, false: 非表示
 * @return {Points} three.js Points
 */
export const setPointsVisible = (points: Points, isVisible: boolean): Points => {
  const clonedPoints = points.clone()
  clonedPoints.visible = isVisible
  return clonedPoints
}

/**
 * 光線生成
 * @return {Raycaster} 光線の太さが0.001の光線インスタンス
 */
export const createRayCaster = () => {
  const raycaster = new Raycaster()
  if (raycaster.params.Points) raycaster.params.Points.threshold = 0.001
  return raycaster
}

/**
 * クリック座標からcanvasの中心座標を(0, 0)としたときに(x,y)を-1〜1の相対位置で取得
 * @param {{x: number, y: number}} clickPosition クリックされた場所の画面左上からのx,y座標
 * @param {{x: number, y: number, width: number, height: number}} clickElement クリックされた要素の左上のx,y座標と幅、高さ
 * @return {{coords: {x: number, y:number}}} {coords: {x: number, y:number}}
 */
export const getClickCoords = (
  clickPosition: { x: number; y: number },
  clickElement: { x: number; y: number; width: number; height: number },
) => {
  //* クリック位置のcanvasからの相対位置を取得
  const relativeX = clickPosition.x - clickElement.x
  const relativeY = clickPosition.y - clickElement.y

  //* canvasの中心座標を(0, 0)としたときの-1〜1での相対位置に変換
  return {
    coords: new Vector2((relativeX / clickElement.width) * 2 - 1, -(relativeY / clickElement.height) * 2 + 1),
  }
}

export const getCameraDistance = (position: Vector3, camera: Camera | null) => {
  if (!camera) {
    return 0
  }

  const distance = camera.position.clone().distanceTo(position)
  return distance
}

// https://docs.google.com/presentation/d/1-_qYSQdIpZ6SeF9G_5n0scfgPz6bTrJhJIVS4wJttcY/edit#slide=id.g1a91a669376_0_0
// Fix the third point (C -> D) to make it place on the normal vector of the line between point 1 (A) and point 2 (B)
export const fixVertexOnNormal = (points: Vector3[]): Vector3[] => {
  if (points.length !== 3) {
    return points
  }

  const directionAB = points[1].clone().sub(points[0]).normalize()
  const directionNormal = points[2].clone().sub(points[1]).cross(directionAB).normalize()
  const directionAD = directionAB.cross(directionNormal).normalize()
  const distance = new Line3(points[0], points[1])
    .closestPointToPoint(points[2], false, new Vector3())
    .distanceTo(points[2])
  const newPoint = points[1].clone().add(directionAD.multiplyScalar(distance))

  return [points[0], points[1], newPoint]
}

// Find the last point of a Parallelogram from previous 3 points
export const findMissingVertexParallelogram = (points: Vector3[]): Vector3 => {
  if (points.length !== 3) {
    return new Vector3()
  }

  // Find the center point between point1 and point3
  const centerPoint = new Line3(points[0].clone(), points[2].clone()).getCenter(new Vector3())
  // Missing point is the point go from point2 across the center point,
  // with 2 times distance from point2 to the center point
  const missingCrossVector = centerPoint.sub(points[1])
  const missingCrossLength = missingCrossVector.length()
  const missingCenterVector = missingCrossVector.normalize().multiplyScalar(missingCrossLength * 2)
  const missingCenterPoint = points[1].clone().add(missingCenterVector)

  return missingCenterPoint
}

/**
 * Convert PointArray to Vector3
 *
 * @param point PointArray to convert
 */
export const pointArrayToVector3 = (point: PointArray) => new Vector3(...point)

/**
 * Convert array of PointArray to array of Vector3
 *
 * @param points Array of PointArray to convert
 */
export const pointsToVector3s = (points: PointArray[]) => points.map(pointArrayToVector3)

const movePointUp = (target: Vector3, planeNormal: Vector3, distance: number) =>
  new Vector3().addVectors(target.clone(), planeNormal.clone().multiplyScalar(distance))

export const getCuboidFromPoints = (points: Vector3[], minSize: number) => {
  const bottomPlane = new Plane().setFromCoplanarPoints(points[0], points[1], points[2])
  const bottomPlaneNormal = bottomPlane.normal.normalize()

  const width = Math.max(minSize, Math.abs(points[0].distanceTo(points[1])))
  const depth = Math.max(minSize, Math.abs(points[1].distanceTo(points[2])))
  const height = bottomPlane.distanceToPoint(points[3])
  const absHeight = Math.max(minSize, Math.abs(height))

  const center = new Line3(points[0], points[2]).getCenter(new Vector3())
  const position = movePointUp(center, bottomPlaneNormal, height / 2)

  // Create orthogonal basis vectors for rotation matrix
  const xAxis = points[0].clone().sub(points[1]).normalize()
  const yAxis = bottomPlaneNormal.clone() // Already normalized
  const zAxis = new Vector3().crossVectors(xAxis, yAxis).normalize()

  // Ensure right-handed coordinate system by recomputing xAxis using a temporary variable
  const newXAxis = new Vector3().crossVectors(yAxis, zAxis).normalize()

  const rotationMatrix = new Matrix4().makeBasis(newXAxis, yAxis, zAxis)
  const quaternion = new Quaternion().setFromRotationMatrix(rotationMatrix)

  // use for drawing bounding box
  const edgesGeometry = new EdgesGeometry(new BoxGeometry(width, absHeight, depth))

  // use for drawing virtual axis
  const startX = new Vector3(-width / 2, 0, 0)
  const endX = new Vector3(width / 2, 0, 0)
  const startY = new Vector3(0, -height / 2, 0)
  const endY = new Vector3(0, height / 2, 0)
  const startZ = new Vector3(0, 0, -depth / 2)
  const endZ = new Vector3(0, 0, depth / 2)

  return {
    width,
    depth,
    height: absHeight,
    position,
    quaternion,
    edgesGeometry,
    rotationMatrix,
    axisPoints: {
      x: [startX, endX],
      y: [startY, endY],
      z: [startZ, endZ],
    },
  }
}

export const rotateCuboid = (cuboid: Cuboid, cuboidDirection?: CuboidDirection): Cuboid => {
  const newCuboid = {
    center: [...cuboid.center] as PointArray,
    extent: [...cuboid.extent] as PointArray,
    rotation: [...cuboid.rotation],
  }
  if (cuboidDirection === EDITOR_CUBOID_DIRECTIONS.X) {
    newCuboid.extent = [newCuboid.extent[1], newCuboid.extent[2], newCuboid.extent[0]]
    const rotationArray = newCuboid.rotation
    newCuboid.rotation = [
      rotationArray[3], // y
      rotationArray[4], // y
      rotationArray[5], // y
      rotationArray[6], // z
      rotationArray[7], // z
      rotationArray[8], // z
      rotationArray[0], // x
      rotationArray[1], // x
      rotationArray[2], // x
    ]
  } else if (cuboidDirection === EDITOR_CUBOID_DIRECTIONS.Y) {
    newCuboid.extent = [newCuboid.extent[2], newCuboid.extent[0], newCuboid.extent[1]]
    const rotationArray = newCuboid.rotation
    newCuboid.rotation = [
      rotationArray[6], // z
      rotationArray[7], // z
      rotationArray[8], // z
      rotationArray[0], // x
      rotationArray[1], // x
      rotationArray[2], // x
      rotationArray[3], // y
      rotationArray[4], // y
      rotationArray[5], // y
    ]
  }
  return newCuboid
}

export const getVerticesFromCuboid = (cuboid: Cuboid) => {
  const centerVector = new Vector3(cuboid.center[0], cuboid.center[1], cuboid.center[2])
  const vertex0 = centerVector
    .clone()
    .add(
      new Vector3(-cuboid.extent[0] / 2, -cuboid.extent[1] / 2, -cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex1 = centerVector
    .clone()
    .add(
      new Vector3(cuboid.extent[0] / 2, -cuboid.extent[1] / 2, -cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex2 = centerVector
    .clone()
    .add(
      new Vector3(cuboid.extent[0] / 2, -cuboid.extent[1] / 2, cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex3 = centerVector
    .clone()
    .add(
      new Vector3(-cuboid.extent[0] / 2, -cuboid.extent[1] / 2, cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex4 = centerVector
    .clone()
    .add(
      new Vector3(-cuboid.extent[0] / 2, cuboid.extent[1] / 2, -cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex5 = centerVector
    .clone()
    .add(
      new Vector3(cuboid.extent[0] / 2, cuboid.extent[1] / 2, -cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )
  const vertex6 = centerVector
    .clone()
    .add(
      new Vector3(cuboid.extent[0] / 2, cuboid.extent[1] / 2, cuboid.extent[2] / 2).applyMatrix3(
        new Matrix3().fromArray(cuboid.rotation),
      ),
    )

  return [vertex0, vertex1, vertex2, vertex3, vertex4, vertex5, vertex6]
}

/**
 * Generate model matrix (world space) for a cuboid so we can calculate each
 * fragment location in the cuboid's local space.
 * Min and max values are also calculated for each cuboid to determine
 * if the fragment is inside the cuboid.
 *
 * @param cuboid Cuboid to generate data
 * @param opacity Opacity value
 */
export const generateCuboidUniforms = (cuboid: Cuboid, opacity = 1) => {
  const { center, rotation, extent } = cuboid

  const boundingBox = new Box3(
    new Vector3(-extent[0] / 2, -extent[1] / 2, -extent[2] / 2),
    new Vector3(extent[0] / 2, extent[1] / 2, extent[2] / 2),
  )

  // Calculate the model matrix
  const modelMatrix = new Matrix4()
  modelMatrix
    .compose(
      new Vector3(...center),
      new Quaternion().setFromRotationMatrix(new Matrix4().setFromMatrix3(new Matrix3().fromArray(rotation))),
      new Vector3(1, 1, 1),
    )
    .invert()

  return {
    modelMatrix,
    min: boundingBox.min,
    max: boundingBox.max,
    opacity,
  }
}

// transformOBBToAABB now lives in @infraloop/common's shared solid-mesh engine (used only there).

// TODO: Remove these converters when cylinder detection result is the same with shape
export const getParametersByShapeType = (
  shapeType: ShapeKey,
  result: CylinderDetectionResult | PolygonDetectionResult,
) => {
  if (shapeType === EDITOR_SHAPE_KEYS.CYLINDERS) {
    const cylinder = result as CylinderDetectionResult
    return { diameter: cylinder.parameters_cylinder?.diameter || 0, length: cylinder.parameters_cylinder?.length || 0 }
  }

  if (shapeType === EDITOR_SHAPE_KEYS.POLYGON) {
    const polygon = result as PolygonDetectionResult
    return {
      vertices: polygon.parameters_polygon?.vertices || [],
      plane_side: polygon.plane_side,
    }
  }

  return {}
}

/**
 * Convert tekkin-detector result to shapes.
 * @param shapeType
 * @param result
 * @returns
 */
export const convertDetectionResultToShapes = (
  shapeType: ShapeKey,
  result: CylinderDetectionResult[] | PolygonDetectionResult[] | ShapeDetectionResult[],
) =>
  result.map((c) => ({
    shape_id: c.shape_id,
    transformation: c.registration_result ? Object.values(c.registration_result.transformation) : [],
    is_shown_on_final_sheet: c.is_shown_on_final_sheet,
    ...getParametersByShapeType(shapeType, c),
  }))

/**
 * Convert 2D points to 3D with Matrix transformation.
 *
 * @param points 2D points
 * @param transformation Transformation matrix
 */
export const transform2Dto3D = (points: Vector2[] | Vector3[], transformation: number[]): PointArray[] => {
  // Convert 2D vertices to 3D positions
  const transformationMatrix = transformation
  const vertices = points.map((v) => [v.x, v.y, v instanceof Vector3 ? v.z : 0, 1]) // Assuming Z-coordinate is 0 for 2D vertices

  // Apply transformation
  const transformedVertices: PointArray[] = []
  for (let i = 0; i < vertices.length; i += 1) {
    const vertex = vertices[i]
    const transformedVertex = [0, 0, 0] as PointArray
    for (let j = 0; j < 3; j += 1) {
      for (let k = 0; k < 4; k += 1) {
        transformedVertex[j] += vertex[k] * transformationMatrix[j * 4 + k]
      }
    }
    transformedVertices.push(transformedVertex)
  }

  return transformedVertices
}

/**
 * Find the center of a set of points.
 * @param points Set of Vector3 points
 * @returns
 */
export const findCenter = (points: Vector3[]): Vector3 | null => {
  if (points.length === 0) {
    return null
  }

  const sumX = points.reduce((acc, point) => acc + point.x, 0)
  const sumY = points.reduce((acc, point) => acc + point.y, 0)
  const sumZ = points.reduce((acc, point) => acc + point.z, 0)

  const centerX = sumX / points.length
  const centerY = sumY / points.length
  const centerZ = sumZ / points.length

  return new Vector3(centerX, centerY, centerZ)
}

/**
 * Convert degrees to radians.
 * @param degrees
 * @returns
 */
export const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180

/**
 * Convert radians to degrees.
 * @param radians
 * @returns
 */
export const radiansToDegrees = (radians: number): number => radians * (180 / Math.PI)

/**
 * Rotate a point by a specific angle.
 *
 * @param point Point to rotate
 * @param angle Angle in radians
 */
export function rotatePoint(point: Vector2 | Vector3, angle: number): Vector2 {
  const newX = point.x * Math.cos(angle) - point.y * Math.sin(angle)
  const newY = point.x * Math.sin(angle) + point.y * Math.cos(angle)
  return new Vector2(newX, newY)
}

/**
 * Rotate a point around a pivot point by a specified Quaternion.
 *
 * @param point
 * @param pivot
 * @param quaternion
 */
export const rotatePointAroundPivot = (point: Vector3, pivot: Vector3, quaternion: Quaternion): Vector3 => {
  const translatedPoint = point.clone().sub(pivot)
  const rotationMatrix = new Matrix4()
  rotationMatrix.makeRotationFromQuaternion(quaternion)
  translatedPoint.applyMatrix4(rotationMatrix)
  translatedPoint.add(pivot)
  return translatedPoint
}

/**
 * Find the orientation of a set of points.
 * @param points - The points to find the orientation of.
 * @returns
 */
export function pointsOrientation(points: Vector3[]): 'clockwise' | 'counterclockwise' | 'collinear' {
  let sum = 0
  const numPoints = points.length

  for (let i = 0; i < numPoints; i += 1) {
    const currPoint = points[i]
    const nextPoint = points[(i + 1) % numPoints]

    sum += (nextPoint.x - currPoint.x) * (nextPoint.y + currPoint.y)
  }

  if (sum > 0) {
    return 'counterclockwise'
  }

  if (sum < 0) {
    return 'clockwise'
  }

  return 'collinear'
}

// Function to calculate the centroid of a set of points
export const getCentroidOfPolygon = (points: Vector2[]): Vector2 => {
  if (!points.length) {
    return new Vector2()
  }

  const sum = points.reduce(
    (acc, point) => {
      acc.x += point.x
      acc.y += point.y
      return acc
    },
    { x: 0, y: 0 },
  )

  return new Vector2(sum.x / points.length, sum.y / points.length)
}

/**
 * Calculates the centroid (geometric center) of a 3D polygon.
 *
 * @param vertices - An array of vertices representing the 3D polygon.
 * @returns  - The centroid of the polygon.
 */
export const getCentroidOfPolygon3d = (vertices: Vector3[]): Vector3 => {
  let centroid = new Vector3()

  // Sum up the coordinates of each vertex
  vertices.forEach((vertex) => {
    centroid.x += vertex.x
    centroid.y += vertex.y
    centroid.z += vertex.z
  })

  // Divide by the total number of vertices to get the average
  const numVertices = vertices.length
  centroid.x /= numVertices
  centroid.y /= numVertices
  centroid.z /= numVertices

  return centroid
}

/**
 * 
/**
 * Finds the point on PlaneB where PointA on PlaneA will be perpendicular to PlaneA.
 *
 * @param {THREE.Vector3} pointA - The point on PlaneA.
 * @param {THREE.Plane} planeA - The plane where PointA is located.
 * @param {THREE.Plane} planeB - The target plane where the projection is to be found.
 * @returns {THREE.Vector3} - The projected point on PlaneB.
 */
export const findPerpendicularProjection = (pointA: Vector3, planeA: Plane, planeB: Plane): Vector3 => {
  // Direction vector is the normal of PlaneA
  const direction = planeA.normal.clone()

  // Calculate the distance from pointA to planeB along the direction vector
  const t = -(planeB.normal.dot(pointA) + planeB.constant) / planeB.normal.dot(direction)

  // Calculate the intersection point
  const projectedPoint = pointA.clone().add(direction.multiplyScalar(t))

  return projectedPoint
}

/**
 * Computes the perpendicular intersection of a point onto a line defined by two points.
 *
 * @param p1 - First point of the line.
 * @param p2 - Second point of the line.
 * @param p3 - Point to project onto the line.
 * @returns The intersection point.
 */
export const getPerpendicularIntersection = (p1: Vector3, p2: Vector3, p3: Vector3): Vector3 => {
  const lineDir = new Vector3().subVectors(p2, p1).normalize() // Line direction unit vector
  const p1ToP3 = new Vector3().subVectors(p3, p1) // Vector from P1 to P3

  const projectionLength = p1ToP3.dot(lineDir) // Projection scalar
  const intersection = new Vector3().addVectors(p1, lineDir.multiplyScalar(projectionLength))

  return intersection
}

/**
 * Calculates the shortest distance from a point to a line defined by two points in 3D space.
 * @param point1 - The first point defining the line.
 * @param point2 - The second point defining the line.
 * @param target - The point to measure the distance from.
 * @param normal - The normal vector of the plane in which the distance is measured.
 * @returns An object containing the distance and the side (1 or -1) indicating which side of the line the point is on.
 */
export const getPointLineIntersection = (
  point1: Vector3,
  point2: Vector3,
  target: Vector3,
  normal: Vector3,
): {
  distance: number
  side: 1 | -1
} => {
  const line = new Vector3().subVectors(point2, point1)
  const targetLine = new Vector3().subVectors(target, point1)
  const crossProd = new Vector3().crossVectors(targetLine, line)
  const side = crossProd.dot(normal) >= 0 ? -1 : 1

  return {
    distance: crossProd.length() / line.length(),
    side,
  }
}

/**
 * Projects a 3D world coordinate to 2D screen coordinates.
 * @param worldPosition - The 3D world position to project.
 * @param camera - The camera being used for the scene.
 * @param screenWidth - Width of the screen (viewport).
 * @param screenHeight - Height of the screen (viewport).
 * @returns The 2D screen coordinates as { x, y }.
 */
export const projectToScreen = (
  worldPosition: Vector3,
  camera: Camera,
  screenWidth: number,
  screenHeight: number,
): Vector2 => {
  // Clone the world position and project it with the camera
  const ndc = worldPosition.clone().project(camera)

  // Map NDC (-1 to 1) to screen coordinates
  const screenX = (ndc.x + 1) * 0.5 * screenWidth
  const screenY = (1 - ndc.y) * 0.5 * screenHeight // Flip Y-axis

  return new Vector2(screenX, screenY)
}

/**
 * Determines the rotation direction of an object based on drag positions.
 * @param center - The center of the object { x, y }.
 * @param start - The initial drag start position { x, y }.
 * @param current - The current drag position { x, y }.
 * @returns 1 for clockwise, -1 for counterclockwise.
 */
export const getRotationDirection = (center: Vector2, start: Vector2, current: Vector2): 1 | -1 => {
  // Vectors from the center to the start and current positions
  const vectorStart = { x: start.x - center.x, y: start.y - center.y }
  const vectorCurrent = { x: current.x - center.x, y: current.y - center.y }

  // Cross product of the two vectors to determine the rotation direction
  const crossProduct = vectorStart.x * vectorCurrent.y - vectorStart.y * vectorCurrent.x

  // If cross product is positive, rotate clockwise; if negative, counterclockwise
  return crossProduct > 0 ? -1 : 1
}

/**
 * Checks if a point is inside a 2D polygon.
 *
 * @param point - The point to check.
 * @param polygon - The polygon to check against.
 */
export const isPointInsidePolygon = (point: Vector2, polygon: Vector2[]): boolean => {
  let isInside = false
  const xs = polygon.map((p) => p.x)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const totalX = Math.abs(maxX - minX)

  for (let curr = 0; curr < polygon.length; curr += 1) {
    const next = (curr + 1) % polygon.length
    const intersect = doLinesIntersect(point, new Vector2(point.x + totalX, point.y), polygon[curr], polygon[next])

    if (intersect) isInside = !isInside
  }

  return isInside
}

/**
 * Checks if a 3D point is inside a 3D polygon by projecting it onto a 2D plane.
 * @param point - The 3D point to check.
 * @param polygon - The 3D polygon to check against.
 * @param planeNormal - The normal vector of the plane onto which to project.
 */
export const isPointInsidePolygon3D = (point: Vector3, polygon: Vector3[], planeNormal: Vector3): boolean => {
  // Project the 3D polygon and point onto a 2D plane
  const projectedPolygon: Vector2[] = []
  const projectedPoint = new Vector2()

  // Choose the dominant axis to ignore for projection
  const absNormal = planeNormal.clone().set(Math.abs(planeNormal.x), Math.abs(planeNormal.y), Math.abs(planeNormal.z))
  let ignoreAxis: 'x' | 'y' | 'z' = 'z'
  if (absNormal.x >= absNormal.y && absNormal.x >= absNormal.z) {
    ignoreAxis = 'x'
  } else if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
    ignoreAxis = 'y'
  }

  // Project polygon vertices
  polygon.forEach((v) => {
    if (ignoreAxis === 'x') {
      projectedPolygon.push(new Vector2(v.y, v.z))
    } else if (ignoreAxis === 'y') {
      projectedPolygon.push(new Vector2(v.x, v.z))
    } else {
      projectedPolygon.push(new Vector2(v.x, v.y))
    }
  })

  // Project point
  if (ignoreAxis === 'x') {
    projectedPoint.set(point.y, point.z)
  } else if (ignoreAxis === 'y') {
    projectedPoint.set(point.x, point.z)
  } else {
    projectedPoint.set(point.x, point.y)
  }

  // Use the 2D point-in-polygon test
  return isPointInsidePolygon(projectedPoint, projectedPolygon)
}

/**
 * Determines the orientation of the ordered triplet (p, q, r).
 * @param p - First point.
 * @param q - Second point.
 * @param r - Third point.
 * @returns 0 if collinear, 1 if clockwise, -1 if counterclockwise.
 */
function orientation(p: Vector2, q: Vector2, r: Vector2): number {
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y)
  if (Math.abs(val) < 1e-9) return 0 // Collinear
  return val > 0 ? 1 : -1 // Clockwise or counterclockwise
}

/**
 * Checks if a point q lies on the segment defined by points p and r.
 * @param p - First endpoint of the segment.
 * @param q - Point to check.
 * @param r - Second endpoint of the segment.
 * @returns True if q lies on the segment pr.
 */
function onSegment(p: Vector2, q: Vector2, r: Vector2): boolean {
  return (
    q.x >= Math.min(p.x, r.x) && q.x <= Math.max(p.x, r.x) && q.y >= Math.min(p.y, r.y) && q.y <= Math.max(p.y, r.y)
  )
}

/**
 * Determines if two line segments intersect.
 * @param p1 - First endpoint of the first segment.
 * @param p2 - Second endpoint of the first segment.
 * @param p3 - First endpoint of the second segment.
 * @param p4 - Second endpoint of the second segment.
 * @returns True if the segments intersect, false otherwise.
 */
function doLinesIntersect(p1: Vector2, p2: Vector2, p3: Vector2, p4: Vector2): boolean {
  // Compute orientations
  const o1 = orientation(p1, p2, p3)
  const o2 = orientation(p1, p2, p4)
  const o3 = orientation(p3, p4, p1)
  const o4 = orientation(p3, p4, p2)

  // General case
  if (o1 !== o2 && o3 !== o4) {
    return true
  }

  // Special cases
  if (o1 === 0 && onSegment(p1, p3, p2)) return true
  if (o2 === 0 && onSegment(p1, p4, p2)) return true
  if (o3 === 0 && onSegment(p3, p1, p4)) return true
  if (o4 === 0 && onSegment(p3, p2, p4)) return true

  return false
}

/**
 * Reset PCD's position and rotation.
 *
 * @param points
 */
export const resetPCD = (points: Points | PointCloudOctree) => {
  // Reset everything first before applying the transformation
  // This is necessary to avoid accumulating transformations
  if (points instanceof Points) {
    points.position.set(0, 0, 0) // Reset position
  }
  points.rotation.set(0, 0, 0) // Reset rotation
  points.scale.set(1, 1, 1) // Reset scale

  points.matrix.identity() // Reset transformation matrix
  points.matrixWorld.identity() // Reset world matrix (if necessary)

  points.updateMatrixWorld(true) // Update the world matrix
}

/**
 * Check if a new point overlaps with existing points within a threshold distance.
 *
 * @param points Existing points
 * @param newPoint New point to check
 * @param threshold Distance to consider as overlapping
 */
export const isNewPointOverlapping = (
  points: PointArray[],
  newPoint: PointArray,
  threshold: number = EDITOR_POINTS_DISTANCE_THRESHOLD,
): boolean =>
  points.some((existingPoint) => {
    const dx = newPoint[0] - existingPoint[0]
    const dy = newPoint[1] - existingPoint[1]
    const distance = Math.sqrt(dx * dx + dy * dy)
    return distance <= threshold
  })
